import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, ViewChild, afterNextRender, inject, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { FaseDaCena } from './cena.model';
import { createOrbitaRenderer, OrbitaRenderer } from './orbita-renderer';

/**
 * O universo WebGL é decorativo. As seis fases continuam botões acessíveis,
 * sobre os mesmos pontos orbitais da cena, inclusive sem aceleração gráfica.
 * O renderizador nasce fora da zone e é liberado junto com o componente.
 */
@Component({
  selector: 'mg-cena-orbital',
  templateUrl: './cena-orbital.component.html',
  styleUrls: ['./cena-orbital.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon],
})
export class CenaOrbitalComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) fases: FaseDaCena[] = [];
  @Input() faseSelecionada = 1;
  @Input() xpPercentual = 0;
  @Input() reduzirMovimento = false;
  @Output() readonly selecionar = new EventEmitter<number>();

  @ViewChild('universo', { static: true }) private universo!: ElementRef<HTMLCanvasElement>;

  private readonly zone = inject(NgZone);
  private renderer: OrbitaRenderer | null = null;
  private observador?: ResizeObserver;
  private destruido = false;
  protected readonly universoAtivo = signal(false);

  constructor() {
    afterNextRender(() => {
      if (this.destruido) return;
      this.zone.runOutsideAngular(() => {
        const canvas = this.universo.nativeElement;
        const estilos = getComputedStyle(canvas);
        this.renderer = createOrbitaRenderer(canvas, {
          selected: this.faseSelecionada,
          progress: this.xpPercentual,
          paused: this.reduzirMovimento || document.hidden,
          palette: {
            primary: estilos.getPropertyValue('--mg-primary').trim(),
            accent: estilos.getPropertyValue('--mg-jornada-acao').trim() || estilos.getPropertyValue('--mg-acento').trim(),
            background: estilos.getPropertyValue('--mg-jornada-fundo').trim() || estilos.getPropertyValue('--mg-shell').trim(),
          },
        });
        if (this.renderer && typeof ResizeObserver !== 'undefined') {
          this.observador = new ResizeObserver(() => this.renderer?.resize());
          this.observador.observe(canvas);
        }
        document.addEventListener('visibilitychange', this.atualizarVisibilidade);
      });
      this.universoAtivo.set(this.renderer !== null);
    });
  }

  ngOnChanges(): void {
    this.zone.runOutsideAngular(() => this.renderer?.update({
      selected: this.faseSelecionada,
      progress: this.xpPercentual,
      paused: this.reduzirMovimento || document.hidden,
    }));
  }

  ngOnDestroy(): void {
    this.destruido = true;
    document.removeEventListener('visibilitychange', this.atualizarVisibilidade);
    this.observador?.disconnect();
    this.renderer?.dispose();
    this.renderer = null;
  }

  private readonly atualizarVisibilidade = (): void => {
    this.renderer?.update({ paused: this.reduzirMovimento || document.hidden });
  };

  /** Ângulo do nó `i` sobre o anel (6 nós, começando no topo), em graus. */
  protected angulo(i: number): string {
    return `${-90 + i * 60}deg`;
  }

  protected get selecionada(): FaseDaCena | undefined {
    return this.fases.find(f => f.numero === this.faseSelecionada);
  }

  /** A fase selecionada ocupa a frente da elipse; os outros nós acompanham. */
  protected get anguloSelecionado(): string {
    return this.angulo(Math.max(0, this.fases.findIndex(f => f.numero === this.faseSelecionada)));
  }

  protected rotulo(fase: FaseDaCena): string {
    const estado = fase.concluida ? ' · Concluída' : fase.atual ? ' · Fase atual' : '';
    return `Fase ${fase.numero} · ${fase.nome}${estado}`;
  }

  protected numero(n: number): string {
    return String(n).padStart(2, '0');
  }
}
